import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getDatabase, ref, get, onValue } from 'firebase/database';
import { collection, getDocs, getFirestore } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  CartesianGrid,
  Cell,
  Legend,
  PieChart,
  Pie,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import Button from 'react-bootstrap/Button';
import Badge from 'react-bootstrap/Badge';
import Modal from 'react-bootstrap/Modal';
import ProgressBar from 'react-bootstrap/ProgressBar';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faClockRotateLeft,
  faCircleNodes,
  faEye,
  faLaptop,
  faMobileScreenButton,
  faPaperPlane,
  faSignal,
  faUsers,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import './VisitorJourneyIntelligence.css';

const SIGNAL_EVENTS = new Set([
  'started_application',
  'application_started',
  'application_field_progressed',
  'application_abandoned',
  'application_submit_review',
  'application_submit_cancelled',
  'submitted_application',
  'application_received',
  'course_selected',
  'course_interest',
]);

const START_EVENTS = new Set(['started_application', 'application_started']);
const PROGRESS_EVENTS = new Set(['application_field_progressed']);
const REVIEW_EVENTS = new Set(['application_submit_review']);
const CANCEL_EVENTS = new Set(['application_submit_cancelled']);
const ABANDON_EVENTS = new Set(['application_abandoned']);
const SUBMIT_EVENTS = new Set(['submitted_application']);
const RECEIVED_EVENTS = new Set(['application_received']);

const COLORS = ['#14b8a6', '#0f766e', '#f59e0b', '#2563eb', '#ef4444', '#8b5cf6', '#22c55e', '#64748b'];
const DEVICE_COLORS = ['#0f172a', '#14b8a6', '#f59e0b', '#2563eb', '#ef4444'];

const emptySummary = {
  totalVisitors: 0,
  returningVisitors: 0,
  totalSessions: 0,
  totalPagesViewed: 0,
  totalEvents: 0,
  liveVisitorsCount: 0,
  applicationStarts: 0,
  applicationProgressSignals: 0,
  applicationReviewSignals: 0,
  applicationCancels: 0,
  applicationAbandons: 0,
  applicationSubmissions: 0,
  applicationReceipts: 0,
  linkedApplications: 0,
  completionRate: 0,
  averageProgress: 0,
  averageSessionSeconds: 0,
};

const asNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const asTimestamp = (value) => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  if (value?.toDate) {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? 0 : date.getTime();
  }
  if (value?.seconds) return value.seconds * 1000;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const shortId = (value) => {
  const text = String(value || '');
  return text.length > 16 ? `${text.slice(0, 8)}...${text.slice(-4)}` : text || 'Unknown';
};

const formatCount = (value) => new Intl.NumberFormat().format(Math.round(asNumber(value)));

const formatPercent = (value) => `${Math.round(asNumber(value) * 10) / 10}%`;

const formatDuration = (seconds) => {
  const value = Math.max(0, Math.round(asNumber(seconds)));
  if (!value) return '0s';
  if (value < 60) return `${value}s`;
  const minutes = Math.floor(value / 60);
  const remainder = value % 60;
  if (minutes < 60) return `${minutes}m ${remainder}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
};

const formatDateTime = (timestamp) => {
  const ms = asTimestamp(timestamp);
  if (!ms) return 'Not recorded';
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatTimeAgo = (timestamp) => {
  const ms = asTimestamp(timestamp);
  if (!ms) return 'just now';
  const diff = Date.now() - ms;
  if (diff < 60 * 1000) return 'just now';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

const dateKey = (timestamp) => {
  const date = new Date(asTimestamp(timestamp));
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const bucketPush = (bucket, key, amount = 1) => {
  const label = String(key || 'Unknown').trim() || 'Unknown';
  bucket[label] = (bucket[label] || 0) + amount;
};

const topEntries = (bucket, limit = 5) =>
  Object.entries(bucket || {})
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

const hasVisitorJourneyData = (visitor = {}, user = {}) => {
  if (!user?.visitorId) return false;

  const sessions = visitor.sessions || {};
  const sessionCount = Object.keys(sessions).length;
  const eventCount = Object.values(sessions).reduce((total, session) => total + Object.keys(session.events || {}).length, 0);

  return Boolean(
    sessionCount ||
    eventCount ||
    visitor.firstSeenAt ||
    visitor.lastSeenAt ||
    visitor.visitCount ||
    visitor.application?.submittedAt ||
    user.analyticsLinkedAt ||
    user.appDate ||
    user.applicationStageUpdatedAt
  );
};

const getSessionStart = (session = {}, visitor = {}) => {
  const candidates = [
    session.startedClientAt,
    session.startedAt,
    visitor.firstSeenAt,
    visitor.lastSeenAt,
  ];

  return candidates.map(asTimestamp).find((value) => value) || 0;
};

const buildJourneyDetail = (visitorId, visitor = {}, user = {}) => {
  const sessionEntries = Object.entries(visitor.sessions || {})
    .map(([sessionId, session]) => {
      const pages = Object.values(session.pages || {})
        .map((page) => ({
          type: 'page',
          timestamp: asTimestamp(page.enteredClientAt || page.entered || session.startedAt || visitor.firstSeenAt),
          label: page.title || page.path || page.page || 'Page view',
          detail: page.path || page.page || '',
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      const events = Object.values(session.events || {})
        .map((event) => ({
          type: 'event',
          timestamp: asTimestamp(event.timestampClientAt || event.timestamp || session.startedAt || visitor.firstSeenAt),
          label: event.eventName || 'event',
          detail: event.page || event.properties?.page || '',
          properties: event.properties || {},
        }))
        .sort((a, b) => a.timestamp - b.timestamp);

      const startAt = getSessionStart(session, visitor);
      const endAt = asTimestamp(session.endedAt || session.lastActivityAt || events[events.length - 1]?.timestamp || pages[pages.length - 1]?.timestamp);

      return {
        sessionId,
        startAt,
        endAt,
        duration: asNumber(session.duration) || Math.max(0, Math.round((endAt - startAt) / 1000)),
        pagesViewed: asNumber(session.pagesViewed) || pages.length,
        eventCount: asNumber(session.eventCount) || events.length,
        landingPage: session.landingPage || pages[0]?.detail || '',
        exitPage: session.exitPage || pages[pages.length - 1]?.detail || '',
        source: session.source || visitor.source || visitor.lastSource || 'Direct',
        events,
        pages,
      };
    })
    .sort((a, b) => a.startAt - b.startAt);

  const timeline = [];
  const pushTimelineItem = (key, label, timestamp, detail, tone) => {
    if (!timestamp) return;
    timeline.push({ key, label, timestamp, detail, tone });
  };

  const allEvents = [];
  sessionEntries.forEach((session) => {
    pushTimelineItem('session', 'Session started', session.startAt, session.landingPage || 'Opened the site', 'neutral');
    session.pages.forEach((page) => {
      allEvents.push({
        timestamp: page.timestamp,
        label: page.label,
        detail: page.detail,
        tone: 'page',
      });
    });
    session.events.forEach((event) => {
      allEvents.push({
        timestamp: event.timestamp,
        label: event.label,
        detail: event.detail,
        tone: event.type === 'event' ? 'event' : 'neutral',
        properties: event.properties,
      });
    });
  });

  const firstSeenAt = asTimestamp(visitor.firstSeenAt || sessionEntries[0]?.startAt || allEvents[0]?.timestamp || user.appDate);
  const linkedAt = asTimestamp(user.analyticsLinkedAt || user.appDate || user.applicationStageUpdatedAt);
  const submittedAt = allEvents.find((item) => item.label === 'submitted_application')?.timestamp || asTimestamp(visitor.application?.submittedAt);
  const receivedAt = allEvents.find((item) => item.label === 'application_received')?.timestamp;
  const startedAt = allEvents.find((item) => ['started_application', 'application_started'].includes(item.label))?.timestamp
    || sessionEntries[0]?.startAt
    || firstSeenAt;

  const firstReviewAt = allEvents.find((item) => item.label === 'application_submit_review')?.timestamp;
  const lastProgressEvent = [...allEvents].reverse().find((item) => item.label === 'application_field_progressed');
  const selectedCourse = [...allEvents].find((item) => item.label === 'course_selected')?.properties?.course || user.course || visitor.lastCourseViewed || '';
  const lastSeenAt = asTimestamp(visitor.lastSeenAt || sessionEntries[sessionEntries.length - 1]?.endAt || submittedAt || linkedAt);
  const journeyDuration = firstSeenAt && lastSeenAt ? Math.max(0, Math.round((lastSeenAt - firstSeenAt) / 1000)) : 0;

  pushTimelineItem('firstSeen', 'First visit', firstSeenAt, visitor.source || visitor.lastSource || 'Direct entry', 'teal');
  pushTimelineItem('started', 'Application opened', startedAt, selectedCourse ? `Interest in ${selectedCourse}` : 'Reached the application flow', 'blue');
  if (lastProgressEvent) {
    pushTimelineItem('progress', 'Progress checkpoint', lastProgressEvent.timestamp, lastProgressEvent.properties?.fieldId || lastProgressEvent.properties?.lastField || 'Form progress recorded', 'amber');
  }
  if (firstReviewAt) {
    pushTimelineItem('review', 'Review confirmed', firstReviewAt, 'Submit review opened', 'violet');
  }
  if (submittedAt) {
    pushTimelineItem('submitted', 'Application submitted', submittedAt, user.applicationStage || 'Submitted', 'green');
  }
  if (receivedAt) {
    pushTimelineItem('received', 'Application received', receivedAt, 'Admissions received the submission', 'green');
  }
  if (linkedAt) {
    pushTimelineItem('joined', 'Account linked', linkedAt, user.applicationStage || 'Student record created', 'dark');
  }

  const milestoneStepCount = [firstSeenAt, startedAt, submittedAt, linkedAt].filter(Boolean).length;
  const journeyProgress = Math.round((milestoneStepCount / 4) * 100);

  const stepper = [
    { key: 'firstVisit', label: 'First visit', timestamp: firstSeenAt, tone: 'teal' },
    { key: 'started', label: 'Application started', timestamp: startedAt, tone: 'blue' },
    { key: 'submitted', label: 'Submitted', timestamp: submittedAt, tone: 'green' },
    { key: 'joined', label: 'Joined', timestamp: linkedAt, tone: 'dark' },
  ].map((step, index) => ({
    ...step,
    index: index + 1,
    active: Boolean(step.timestamp),
  }));

  const signals = allEvents
    .filter((item) => SIGNAL_EVENTS.has(item.label))
    .sort((a, b) => a.timestamp - b.timestamp)
    .slice(0, 30);

  return {
    visitorId,
    userId: user.id || user.userId || '',
    name: user.bio || user.fullName || user.name || 'Unnamed applicant',
    course: user.course || selectedCourse || 'Course not set',
    stage: user.applicationStage || user.userStatus || 'Unknown',
    source: visitor.source || visitor.lastSource || user.leadSource || user.hear || 'Direct',
    country: visitor.country || '',
    city: visitor.city || '',
    region: visitor.region || '',
    device: visitor.device || 'Unknown',
    browser: visitor.browser || 'Unknown',
    operatingSystem: visitor.operatingSystem || 'Unknown',
    emailStatus: user.emailStatus || 'Unknown',
    smsStatus: user.smsStatus || 'Unknown',
    linkedAt,
    submittedAt,
    firstSeenAt,
    startedAt,
    lastSeenAt,
    journeyDuration,
    journeyProgress,
    milestoneStepCount,
    timeline,
    sessions: sessionEntries,
    stepper,
    selectedCourse,
    pagesViewed: sessionEntries.reduce((sum, session) => sum + session.pagesViewed, 0),
    eventsCount: sessionEntries.reduce((sum, session) => sum + session.eventCount, 0),
    totalSessions: sessionEntries.length,
    firstReviewAt,
    signals,
  };
};

const padDailySeries = (bucket) => {
  const dates = Object.keys(bucket).sort();
  if (!dates.length) {
    return Array.from({ length: 7 }, (_, index) => ({
      date: `Day ${index + 1}`,
      started: 0,
      progressed: 0,
      abandons: 0,
      submitted: 0,
      received: 0,
    }));
  }

  const lastSeven = dates.slice(-14);
  return lastSeven.map((date) => ({
    date,
    started: bucket[date]?.started || 0,
    progressed: bucket[date]?.progressed || 0,
    review: bucket[date]?.review || 0,
    cancels: bucket[date]?.cancels || 0,
    abandons: bucket[date]?.abandons || 0,
    submitted: bucket[date]?.submitted || 0,
    received: bucket[date]?.received || 0,
  }));
};

const buildJourneySummary = (visitors = {}, liveVisitors = {}, users = []) => {
  const summary = { ...emptySummary };
  const sourceCounts = {};
  const countryCounts = {};
  const pageCounts = {};
  const deviceCounts = {};
  const browserCounts = {};
  const courseCounts = {};
  const abandonmentStepCounts = {};
  const abandonmentFieldCounts = {};
  const dailyBuckets = {};
  const sessionDurations = [];
  const progressSamples = [];
  const signalFeed = [];
  const funnelSessions = {
    started: new Set(),
    progressed: new Set(),
    reviewed: new Set(),
    cancelled: new Set(),
    abandoned: new Set(),
    submitted: new Set(),
    received: new Set(),
  };

  let liveNow = 0;

  Object.entries(visitors || {}).forEach(([visitorId, visitor]) => {
    summary.totalVisitors += 1;
    if (visitor.returningVisitor || asNumber(visitor.visitCount) > 1) {
      summary.returningVisitors += 1;
    }

    bucketPush(sourceCounts, visitor.source || visitor.lastSource || 'Direct');
    bucketPush(countryCounts, visitor.country || 'Unknown');
    bucketPush(deviceCounts, visitor.device || 'Unknown');
    bucketPush(browserCounts, visitor.browser || 'Unknown');

    Object.values(visitor.interests || {}).forEach((interest) => {
      bucketPush(courseCounts, interest.course || interest.__key || 'Unknown', asNumber(interest.visits) || 1);
    });

    Object.entries(visitor.sessions || {}).forEach(([sessionId, session]) => {
      summary.totalSessions += 1;
      summary.totalPagesViewed += asNumber(session.pagesViewed);
      summary.totalEvents += asNumber(session.eventCount);
      if (session.duration) {
        sessionDurations.push(asNumber(session.duration));
      }

      const sessionKey = `${visitorId}:${sessionId}`;

      Object.values(session.pages || {}).forEach((page) => {
        const label = page.path || page.page || page.title || 'Unknown page';
        bucketPush(pageCounts, label);
      });

      Object.values(session.events || {}).forEach((event) => {
        const eventName = String(event.eventName || '').trim();
        const timestamp = asTimestamp(event.timestamp || event.timestampClientAt || session.startedAt || visitor.firstSeenAt || visitor.lastSeenAt);
        const props = event.properties || {};

        if (timestamp && SIGNAL_EVENTS.has(eventName)) {
          signalFeed.push({
            timestamp,
            eventName,
            visitorId,
            sessionId,
            page: event.page || props.page || session.exitPage || '',
            title: event.title || props.title || '',
            properties: props,
          });
        }

        if (START_EVENTS.has(eventName)) {
          funnelSessions.started.add(sessionKey);
        }
        if (PROGRESS_EVENTS.has(eventName)) {
          funnelSessions.progressed.add(sessionKey);
        }
        if (REVIEW_EVENTS.has(eventName)) {
          funnelSessions.reviewed.add(sessionKey);
        }
        if (CANCEL_EVENTS.has(eventName)) {
          funnelSessions.cancelled.add(sessionKey);
        }
        if (ABANDON_EVENTS.has(eventName)) {
          funnelSessions.abandoned.add(sessionKey);
          if (props.lastStepReached) {
            bucketPush(abandonmentStepCounts, `Step ${props.lastStepReached}`);
          }
          if (props.lastField) {
            bucketPush(abandonmentFieldCounts, props.lastField);
          }
        }
        if (SUBMIT_EVENTS.has(eventName)) {
          funnelSessions.submitted.add(sessionKey);
          if (props.progressPercent !== undefined) {
            progressSamples.push(asNumber(props.progressPercent));
          }
        }
        if (RECEIVED_EVENTS.has(eventName)) {
          funnelSessions.received.add(sessionKey);
        }

        if (typeof props.progressPercent === 'number') {
          progressSamples.push(asNumber(props.progressPercent));
        }

        if (eventName === 'course_interest' && props.course) {
          bucketPush(courseCounts, props.course);
        }

        if (timestamp) {
          const day = dateKey(timestamp);
          if (!dailyBuckets[day]) {
            dailyBuckets[day] = {
              started: 0,
              progressed: 0,
              review: 0,
              cancels: 0,
              abandons: 0,
              submitted: 0,
              received: 0,
            };
          }
          if (START_EVENTS.has(eventName)) dailyBuckets[day].started += 1;
          if (PROGRESS_EVENTS.has(eventName)) dailyBuckets[day].progressed += 1;
          if (REVIEW_EVENTS.has(eventName)) dailyBuckets[day].review += 1;
          if (CANCEL_EVENTS.has(eventName)) dailyBuckets[day].cancels += 1;
          if (ABANDON_EVENTS.has(eventName)) dailyBuckets[day].abandons += 1;
          if (SUBMIT_EVENTS.has(eventName)) dailyBuckets[day].submitted += 1;
          if (RECEIVED_EVENTS.has(eventName)) dailyBuckets[day].received += 1;
        }
      });
    });
  });

  (liveVisitors || []).forEach(() => {
    liveNow += 1;
  });

  users.forEach((user) => {
    if (user.visitorId) {
      summary.linkedApplications += 1;
    }
  });

  const averageProgress = progressSamples.length
    ? progressSamples.reduce((sum, value) => sum + value, 0) / progressSamples.length
    : 0;

  const averageSessionSeconds = sessionDurations.length
    ? sessionDurations.reduce((sum, value) => sum + value, 0) / sessionDurations.length
    : 0;

  summary.liveVisitorsCount = liveNow;
  summary.applicationStarts = funnelSessions.started.size;
  summary.applicationProgressSignals = funnelSessions.progressed.size;
  summary.applicationReviewSignals = funnelSessions.reviewed.size;
  summary.applicationCancels = funnelSessions.cancelled.size;
  summary.applicationAbandons = funnelSessions.abandoned.size;
  summary.applicationSubmissions = funnelSessions.submitted.size;
  summary.applicationReceipts = funnelSessions.received.size;
  summary.completionRate = summary.applicationStarts ? (summary.applicationSubmissions / summary.applicationStarts) * 100 : 0;
  summary.averageProgress = averageProgress;
  summary.averageSessionSeconds = averageSessionSeconds;

  const funnelData = [
    { label: 'Visitors', value: summary.totalVisitors, detail: 'unique visitors' },
    { label: 'Application starts', value: summary.applicationStarts, detail: 'sessions that opened the form' },
    { label: 'Progress signals', value: summary.applicationProgressSignals, detail: 'fields completed' },
    { label: 'Review clicks', value: summary.applicationReviewSignals, detail: 'submit reviews' },
    { label: 'Submitted', value: summary.applicationSubmissions, detail: 'applications sent' },
    { label: 'Linked accounts', value: summary.linkedApplications, detail: 'student records matched' },
  ];

  const liveRows = (liveVisitors || [])
    .map((visitor) => ({
      id: visitor.visitorId || visitor.id || '',
      visitorId: visitor.visitorId || visitor.id || '',
      sessionId: visitor.sessionId || '',
      currentPage: visitor.currentPage || '',
      currentTitle: visitor.currentTitle || '',
      country: visitor.country || '',
      city: visitor.city || '',
      region: visitor.region || '',
      device: visitor.device || 'Unknown',
      browser: visitor.browser || 'Unknown',
      operatingSystem: visitor.operatingSystem || 'Unknown',
      referral: visitor.referral || '',
      lastSeenAt: asTimestamp(visitor.lastSeenAt || visitor.lastSeenClientAt),
    }))
    .sort((a, b) => b.lastSeenAt - a.lastSeenAt);

  const recentSignals = signalFeed
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 24)
    .map((signal) => ({
      ...signal,
      summary: signal.properties?.progressPercent !== undefined
        ? `${Math.round(asNumber(signal.properties.progressPercent))}%`
        : signal.properties?.lastStepReached
          ? `Step ${signal.properties.lastStepReached}`
          : signal.properties?.course
            ? signal.properties.course
            : '',
    }));

  const recentApplications = users
    .map((user) => {
      const visitor = visitors[user.visitorId] || {};
      if (!hasVisitorJourneyData(visitor, user)) {
        return null;
      }

      const joinedAt = asTimestamp(user.analyticsLinkedAt || user.appDate || user.applicationStageUpdatedAt);
      const lastSeenAt = asTimestamp(visitor.lastSeenAt || joinedAt || visitor.firstSeenAt);
      const firstSeenAt = asTimestamp(visitor.firstSeenAt || joinedAt || visitor.lastSeenAt);
      const sessionCount = Object.keys(visitor.sessions || {}).length;
      const eventCount = Object.values(visitor.sessions || {}).reduce((total, session) => total + Object.keys(session.events || {}).length, 0);

      return {
        id: user.id || user.userId || shortId(user.visitorId || user.email || ''),
        userId: user.id || user.userId || '',
        bio: user.bio || user.fullName || user.name || '',
        fullName: user.fullName || user.bio || user.name || '',
        name: user.bio || user.fullName || user.name || 'Unnamed applicant',
        course: user.course || 'Course not set',
        stage: user.applicationStage || user.userStatus || 'Unknown',
        applicationStage: user.applicationStage || '',
        emailStatus: user.emailStatus || 'Unknown',
        smsStatus: user.smsStatus || 'Unknown',
        source: user.leadSource || user.hear || user.referralCode || visitor.source || visitor.lastSource || 'Unknown',
        leadSource: user.leadSource || user.hear || '',
        hear: user.hear || '',
        visitorId: user.visitorId || '',
        joinedAt,
        analyticsLinkedAt: joinedAt || null,
        appDate: user.appDate || null,
        applicationStageUpdatedAt: user.applicationStageUpdatedAt || null,
        firstSeenAt,
        lastSeenAt,
        sessionCount,
        eventCount,
        country: visitor.country || '',
        city: visitor.city || '',
        region: visitor.region || '',
        device: visitor.device || '',
        browser: visitor.browser || '',
        operatingSystem: visitor.operatingSystem || '',
        hasJourneyData: true,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (b.joinedAt || b.lastSeenAt || 0) - (a.joinedAt || a.lastSeenAt || 0))
    .slice(0, 8);

  const dailySeries = padDailySeries(dailyBuckets);

  return {
    ...summary,
    sourceCounts: topEntries(sourceCounts, 6),
    countryCounts: topEntries(countryCounts, 6),
    pageCounts: topEntries(pageCounts, 6),
    deviceCounts: topEntries(deviceCounts, 5),
    browserCounts: topEntries(browserCounts, 5),
    courseCounts: topEntries(courseCounts, 6),
    abandonmentStepCounts: topEntries(abandonmentStepCounts, 5),
    abandonmentFieldCounts: topEntries(abandonmentFieldCounts, 5),
    dailySeries,
    funnelData,
    liveRows,
    recentSignals,
    recentApplications,
  };
};

const renderTopList = (items, emptyText) => {
  if (!items.length) {
    return <div className="visitorJourneyEmpty">{emptyText}</div>;
  }

  const max = Math.max(...items.map((item) => item.value), 1);

  return (
    <div className="visitorJourneyBars">
      {items.map((item) => (
        <div key={item.label} className="visitorJourneyBarRow">
          <div className="visitorJourneyBarRowHead">
            <span>{item.label}</span>
            <strong>{formatCount(item.value)}</strong>
          </div>
          <div className="visitorJourneyBarTrack">
            <span style={{ width: `${Math.max(5, (item.value / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

function VisitorJourneyIntelligence() {
  const db = useMemo(() => getDatabase(app), []);
  const firestore = useMemo(() => getFirestore(app), []);
  const rawJourneyRef = useRef({ visitors: {}, users: [] });
  const liveVisitorsRef = useRef([]);
  const [analytics, setAnalytics] = useState({
    ...emptySummary,
    liveRows: [],
    sourceCounts: [],
    countryCounts: [],
    pageCounts: [],
    deviceCounts: [],
    browserCounts: [],
    courseCounts: [],
    abandonmentStepCounts: [],
    abandonmentFieldCounts: [],
    dailySeries: [],
    funnelData: [],
    recentSignals: [],
    recentApplications: [],
  });
  const [showJourneyModal, setShowJourneyModal] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const openJourneyModal = useCallback((row) => {
    const rawVisitors = rawJourneyRef.current.visitors || {};
    const visitor = rawVisitors[row.visitorId] || {};
    setSelectedJourney(buildJourneyDetail(row.visitorId, visitor, row));
    setShowJourneyModal(true);
  }, []);

  const closeJourneyModal = useCallback(() => {
    setShowJourneyModal(false);
  }, []);

  const loadAnalytics = useCallback(async () => {
    setRefreshing(true);
    setError('');

    try {
      const [visitorsSnap, usersSnap] = await Promise.all([
        get(ref(db, 'analytics/visitors')),
        getDocs(collection(firestore, 'users')),
      ]);

      const visitors = visitorsSnap.exists() ? visitorsSnap.val() : {};
      const users = [];
      usersSnap.forEach((docItem) => {
        users.push({ id: docItem.id, ...docItem.data() });
      });

      rawJourneyRef.current = { visitors, users };
      setAnalytics(buildJourneySummary(visitors, liveVisitorsRef.current, users));
    } catch (fetchError) {
      console.error('Failed to load visitor journey analytics:', fetchError);
      setError('We could not load the visitor journey data right now.');
      setAnalytics({
        ...emptySummary,
        liveRows: [],
        sourceCounts: [],
        countryCounts: [],
        pageCounts: [],
        deviceCounts: [],
        browserCounts: [],
        courseCounts: [],
        abandonmentStepCounts: [],
        abandonmentFieldCounts: [],
        dailySeries: [],
        funnelData: [],
        recentSignals: [],
        recentApplications: [],
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db, firestore]);

  useEffect(() => {
    loadAnalytics();

    const liveRef = ref(db, 'analytics/live');
    const unsubscribe = onValue(liveRef, (snapshot) => {
      const liveMap = snapshot.val() || {};
      const rows = Object.entries(liveMap).map(([visitorId, visitor]) => ({
        id: visitorId,
        visitorId,
        sessionId: visitor.sessionId || '',
        currentPage: visitor.currentPage || '',
        currentTitle: visitor.currentTitle || '',
        country: visitor.country || '',
        city: visitor.city || '',
        region: visitor.region || '',
        device: visitor.device || 'Unknown',
        browser: visitor.browser || 'Unknown',
        operatingSystem: visitor.operatingSystem || 'Unknown',
        referral: visitor.referral || '',
        lastSeenAt: asTimestamp(visitor.lastSeenAt || visitor.lastSeenClientAt),
      })).sort((a, b) => b.lastSeenAt - a.lastSeenAt);

      liveVisitorsRef.current = rows;
      setAnalytics((current) => ({
        ...current,
        liveRows: rows,
        liveVisitorsCount: rows.length,
      }));
    }, (listenerError) => {
      console.error('Failed to subscribe to live visitors:', listenerError);
      liveVisitorsRef.current = [];
      setAnalytics((current) => ({
        ...current,
        liveRows: [],
        liveVisitorsCount: 0,
      }));
    });

    return () => unsubscribe();
  }, [db, loadAnalytics]);

  const heroMetrics = useMemo(() => [
    { label: 'Live now', value: analytics.liveVisitorsCount, icon: faSignal, tone: 'teal' },
    { label: 'Visitors', value: analytics.totalVisitors, icon: faUsers, tone: 'ink' },
    { label: 'Starts', value: analytics.applicationStarts, icon: faEye, tone: 'amber' },
    { label: 'Progress', value: analytics.applicationProgressSignals, icon: faCircleNodes, tone: 'blue' },
    { label: 'Submitted', value: analytics.applicationSubmissions, icon: faPaperPlane, tone: 'green' },
    { label: 'Abandons', value: analytics.applicationAbandons, icon: faTriangleExclamation, tone: 'red' },
  ], [analytics]);

  const deviceChartData = useMemo(() => analytics.deviceCounts.map((item) => ({ name: item.label, value: item.value })), [analytics.deviceCounts]);

  const stageChartData = useMemo(() => analytics.funnelData.map((item) => ({
    ...item,
    ratio: analytics.applicationStarts ? (item.value / analytics.applicationStarts) * 100 : 0,
  })), [analytics.funnelData, analytics.applicationStarts]);

  const completionRate = Math.max(0, Math.min(100, analytics.completionRate || 0));
  const averageProgress = Math.max(0, Math.min(100, analytics.averageProgress || 0));

  return (
    <div className="mainContainer">
      <LeftBar />
      <div className="theMainContainer">
        <Dashnav />
        <div className="visitorJourneyPage">
          <div className="visitorJourneyHero">
            <div className="visitorJourneyHeroTop">
              <div>
                <p className="visitorJourneyKicker">Data Analytics</p>
                <h2>Visitor Journey Intelligence</h2>
                <p className="visitorJourneyLead">
                  A live command center for traffic, application progress, abandonment points, and admissions handoff.
                </p>
              </div>
              <div className="visitorJourneyHeroActions">
                <Button
                  variant="light"
                  className="visitorJourneyRefreshBtn"
                  onClick={loadAnalytics}
                  disabled={loading || refreshing}
                >
                  <FontAwesomeIcon icon={faArrowsRotate} className={refreshing ? 'visitorJourneySpinning' : ''} />
                  <span>{refreshing ? 'Refreshing' : 'Refresh'}</span>
                </Button>
              </div>
            </div>

            <div className="visitorJourneyHeroSummary">
              <div className="visitorJourneyHeroSummaryMeta">
                <Badge bg="dark" className="visitorJourneySummaryBadge">Completion {formatPercent(completionRate)}</Badge>
                <span>{formatCount(analytics.applicationSubmissions)} submissions tracked</span>
              </div>
              <ProgressBar now={completionRate} className="visitorJourneyHeroProgress" />
              <div className="visitorJourneyHeroSummaryFooter">
                <div>
                  <span>Average completion signal</span>
                  <strong>{formatPercent(averageProgress)}</strong>
                </div>
                <div>
                  <span>Average session</span>
                  <strong>{Math.round(analytics.averageSessionSeconds || 0)}s</strong>
                </div>
                <div>
                  <span>Linked applications</span>
                  <strong>{formatCount(analytics.linkedApplications)}</strong>
                </div>
              </div>
            </div>

            <div className="visitorJourneyStats">
              {heroMetrics.map((metric) => (
                <div key={metric.label} className={`visitorJourneyStat visitorJourneyStat--${metric.tone}`}>
                  <div className="visitorJourneyStatIcon">
                    <FontAwesomeIcon icon={metric.icon} />
                  </div>
                  <span>{metric.label}</span>
                  <strong>{formatCount(metric.value)}</strong>
                </div>
              ))}
            </div>
          </div>

          {error && <div className="visitorJourneyError">{error}</div>}

          {loading ? (
            <div className="visitorJourneyLoading">
              <Spinner animation="border" role="status" />
              <p>Loading visitor journey data...</p>
            </div>
          ) : (
            <>
              <div className="visitorJourneyGrid">
                <section className="visitorJourneyPanel visitorJourneyPanel--wide">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Journey funnel</p>
                      <h4>How visitors move from interest to submission</h4>
                    </div>
                    <Badge bg="light" text="dark" className="visitorJourneyPanelBadge">
                      {formatPercent(completionRate)} conversion from start to submit
                    </Badge>
                  </div>
                  <div className="visitorJourneyChart">
                    <ResponsiveContainer width="100%" height={330}>
                      <BarChart data={stageChartData} layout="vertical" margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis type="number" allowDecimals={false} />
                        <YAxis dataKey="label" type="category" width={150} />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="value" name="Sessions" radius={[0, 8, 8, 0]}>
                          {stageChartData.map((entry, index) => (
                            <Cell key={entry.label} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="visitorJourneyMiniStats">
                    {stageChartData.map((item) => (
                      <div key={item.label} className="visitorJourneyMiniStat">
                        <span>{item.label}</span>
                        <strong>{formatCount(item.value)}</strong>
                        <small>{formatPercent(item.ratio || 0)} of starts</small>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Live pulse</p>
                      <h4>People on the site right now</h4>
                    </div>
                    <Badge bg="success">Live {formatCount(analytics.liveVisitorsCount)}</Badge>
                  </div>
                  <div className="visitorJourneyLiveList">
                    {analytics.liveRows.length ? analytics.liveRows.slice(0, 8).map((row) => (
                      <div key={`${row.visitorId}-${row.sessionId}`} className="visitorJourneyLiveRow">
                        <div className="visitorJourneyLiveRowMain">
                          <strong>{row.currentTitle || row.currentPage || 'Unknown page'}</strong>
                          <span>{row.currentPage || 'No page recorded'}</span>
                        </div>
                        <div className="visitorJourneyLiveRowMeta">
                          <Badge bg="dark">{[row.city, row.region, row.country].filter(Boolean).join(', ') || 'Unknown location'}</Badge>
                          <small>{row.device}</small>
                          <small>{formatTimeAgo(row.lastSeenAt)}</small>
                        </div>
                      </div>
                    )) : (
                      <div className="visitorJourneyEmpty">No live visitors right now.</div>
                    )}
                  </div>
                </section>
              </div>

              <div className="visitorJourneyGrid visitorJourneyGrid--secondary">
                <section className="visitorJourneyPanel visitorJourneyPanel--wide">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Daily rhythm</p>
                      <h4>Starts, progress, abandons, and submissions over time</h4>
                    </div>
                    <Badge bg="dark">{analytics.dailySeries.length} days</Badge>
                  </div>
                  <div className="visitorJourneyChart">
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={analytics.dailySeries} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="journeyStarts" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.45} />
                            <stop offset="95%" stopColor="#14b8a6" stopOpacity={0.03} />
                          </linearGradient>
                          <linearGradient id="journeySubmits" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.45} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
                          </linearGradient>
                          <linearGradient id="journeyAbandons" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#ef4444" stopOpacity={0.45} />
                            <stop offset="95%" stopColor="#ef4444" stopOpacity={0.03} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="date" />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Legend />
                        <Area type="monotone" dataKey="started" stroke="#14b8a6" fill="url(#journeyStarts)" name="Starts" />
                        <Area type="monotone" dataKey="submitted" stroke="#2563eb" fill="url(#journeySubmits)" name="Submitted" />
                        <Area type="monotone" dataKey="abandons" stroke="#ef4444" fill="url(#journeyAbandons)" name="Abandons" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </section>

                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Device mix</p>
                      <h4>Who is using the form</h4>
                    </div>
                    <Badge bg="light" text="dark">Devices</Badge>
                  </div>
                  <div className="visitorJourneyChart visitorJourneyChart--compact">
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={deviceChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={95}
                          paddingAngle={3}
                        >
                          {deviceChartData.map((entry, index) => (
                            <Cell key={`device-${entry.name}`} fill={DEVICE_COLORS[index % DEVICE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="visitorJourneyChipGrid">
                    {analytics.deviceCounts.map((device) => (
                      <div key={device.label} className="visitorJourneyChip">
                        <FontAwesomeIcon icon={device.label.toLowerCase().includes('mobile') ? faMobileScreenButton : faLaptop} />
                        <span>{device.label}</span>
                        <strong>{formatCount(device.value)}</strong>
                      </div>
                    ))}
                  </div>
                </section>
              </div>

              <div className="visitorJourneyGrid visitorJourneyGrid--secondary">
                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Top sources</p>
                      <h4>Where visitors are arriving from</h4>
                    </div>
                  </div>
                  {renderTopList(analytics.sourceCounts, 'No source data yet.')}
                </section>

                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Top countries</p>
                      <h4>Audience geography</h4>
                    </div>
                  </div>
                  {renderTopList(analytics.countryCounts, 'No country data yet.')}
                </section>
              </div>

              <div className="visitorJourneyGrid visitorJourneyGrid--secondary">
                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Top courses</p>
                      <h4>What visitors are leaning toward</h4>
                    </div>
                  </div>
                  {renderTopList(analytics.courseCounts, 'No course interest data yet.')}
                </section>

                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Browser mix</p>
                      <h4>How they are accessing the site</h4>
                    </div>
                  </div>
                  {renderTopList(analytics.browserCounts, 'No browser data yet.')}
                </section>
              </div>

              <div className="visitorJourneyGrid visitorJourneyGrid--secondary">
                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Abandonment hotspots</p>
                      <h4>Where people are falling away</h4>
                    </div>
                  </div>
                  {renderTopList(analytics.abandonmentStepCounts, 'No abandonment checkpoints yet.')}
                  <div className="visitorJourneySpacer" />
                  {renderTopList(analytics.abandonmentFieldCounts, 'No field-level drop-off yet.')}
                </section>

                <section className="visitorJourneyPanel">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Top pages</p>
                      <h4>Most viewed pages in the journey</h4>
                    </div>
                  </div>
                  {renderTopList(analytics.pageCounts, 'No page view data yet.')}
                </section>
              </div>

              <div className="visitorJourneyGrid visitorJourneyGrid--secondary">
                <section className="visitorJourneyPanel visitorJourneyPanel--wide visitorJourneyPanel--fullWidth">
                  <div className="visitorJourneyPanelHeader">
                    <div>
                      <p className="visitorJourneyPanelKicker">Admissions handoff</p>
                      <h4>Recent application records linked from analytics</h4>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table hover className="visitorJourneyTable">
                      <thead>
                        <tr>
                          <th>Applicant</th>
                          <th>Course</th>
                          <th>Stage</th>
                          <th>Email</th>
                          <th>SMS</th>
                          <th>Analytics</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recentApplications.length ? analytics.recentApplications.map((row) => (
                          <tr key={row.id}>
                            <td>
                              <strong>{row.name}</strong>
                              <div className="visitorJourneyTableSubtext">
                                {row.visitorId ? `Visitor ${shortId(row.visitorId)}` : row.source}
                              </div>
                            </td>
                            <td>{row.course}</td>
                            <td><Badge bg="dark">{row.stage}</Badge></td>
                            <td><Badge bg={row.emailStatus === 'sent' ? 'success' : row.emailStatus === 'failed' ? 'danger' : 'secondary'}>{row.emailStatus}</Badge></td>
                            <td><Badge bg={row.smsStatus === 'sent' ? 'success' : row.smsStatus === 'failed' ? 'danger' : 'secondary'}>{row.smsStatus}</Badge></td>
                            <td>
                              <Button
                                variant="outline-dark"
                                size="sm"
                                className="visitorJourneyTableActionBtn"
                                onClick={() => openJourneyModal(row)}
                                disabled={!row.hasJourneyData}
                              >
                                View analytics data
                              </Button>
                            </td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="6" className="visitorJourneyEmptyCell">No linked applications found yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </section>
              </div>

              <section className="visitorJourneyPanel visitorJourneyFeedPanel">
                <div className="visitorJourneyPanelHeader">
                  <div>
                    <p className="visitorJourneyPanelKicker">Signal feed</p>
                    <h4>Latest visitor and application events</h4>
                  </div>
                  <Badge bg="light" text="dark">
                    <FontAwesomeIcon icon={faClockRotateLeft} className="me-1" />
                    Latest signals
                  </Badge>
                </div>
                <div className="table-responsive">
                  <Table hover className="visitorJourneyTable visitorJourneyTable--signal">
                    <thead>
                      <tr>
                        <th>When</th>
                        <th>Signal</th>
                        <th>Visitor</th>
                        <th>Session</th>
                        <th>Page</th>
                        <th>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.recentSignals.length ? analytics.recentSignals.map((signal) => (
                        <tr key={`${signal.visitorId}-${signal.sessionId}-${signal.timestamp}`}>
                          <td>{formatTimeAgo(signal.timestamp)}</td>
                          <td>
                            <Badge bg={
                              signal.eventName === 'submitted_application' ? 'success'
                                : signal.eventName === 'application_abandoned' ? 'danger'
                                  : signal.eventName === 'application_field_progressed' ? 'primary'
                                    : signal.eventName === 'application_submit_review' ? 'warning'
                                      : 'dark'
                            }>
                              {signal.eventName}
                            </Badge>
                          </td>
                          <td>{shortId(signal.visitorId)}</td>
                          <td>{shortId(signal.sessionId)}</td>
                          <td>{signal.page || 'Unknown'}</td>
                          <td>{signal.summary || '—'}</td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="visitorJourneyEmptyCell">No signals recorded yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </section>
            </>
          )}
        </div>
      </div>

      <Modal
        show={showJourneyModal}
        onHide={closeJourneyModal}
        size="xl"
        centered
        scrollable
        fullscreen="lg-down"
        className="visitorJourneyModal"
      >
        <Modal.Header closeButton>
          <div className="visitorJourneyModalHeader">
            <p className="visitorJourneyPanelKicker">Visitor Journey Intelligence</p>
            <h4>{selectedJourney?.name || 'Visitor journey'}</h4>
            <p className="visitorJourneyModalSubtext">
              {selectedJourney?.course || 'Course not set'} · {selectedJourney?.stage || 'Unknown stage'}
            </p>
          </div>
        </Modal.Header>
        <Modal.Body>
          {selectedJourney ? (
            <div className="visitorJourneyModalBody">
              <div className="visitorJourneyModalIdentityRow">
                <div className="visitorJourneyModalIdentity">
                  <div className="visitorJourneyModalAvatar">
                    <FontAwesomeIcon icon={faUsers} />
                  </div>
                  <div>
                    <strong>{selectedJourney.name}</strong>
                    <div className="visitorJourneyTableSubtext">
                      Visitor {shortId(selectedJourney.visitorId)} · Session trail {selectedJourney.totalSessions}
                    </div>
                  </div>
                </div>
                <div className="visitorJourneyModalBadges">
                  <Badge bg="dark">{selectedJourney.source || 'Direct'}</Badge>
                  {[selectedJourney.city, selectedJourney.region, selectedJourney.country].filter(Boolean).length ? (
                    <Badge bg="secondary">{[selectedJourney.city, selectedJourney.region, selectedJourney.country].filter(Boolean).join(', ')}</Badge>
                  ) : null}
                  <Badge bg={selectedJourney.emailStatus === 'sent' ? 'success' : selectedJourney.emailStatus === 'failed' ? 'danger' : 'light'} text={selectedJourney.emailStatus === 'sent' || selectedJourney.emailStatus === 'failed' ? 'white' : 'dark'}>
                    Email {selectedJourney.emailStatus}
                  </Badge>
                  <Badge bg={selectedJourney.smsStatus === 'sent' ? 'success' : selectedJourney.smsStatus === 'failed' ? 'danger' : 'light'} text={selectedJourney.smsStatus === 'sent' || selectedJourney.smsStatus === 'failed' ? 'white' : 'dark'}>
                    SMS {selectedJourney.smsStatus}
                  </Badge>
                </div>
              </div>

              <div className="visitorJourneyModalStats">
                <div className="visitorJourneyModalStat">
                  <span>First visit</span>
                  <strong>{formatDateTime(selectedJourney.firstSeenAt)}</strong>
                </div>
                <div className="visitorJourneyModalStat">
                  <span>Application started</span>
                  <strong>{formatDateTime(selectedJourney.startedAt)}</strong>
                </div>
                <div className="visitorJourneyModalStat">
                  <span>Submitted</span>
                  <strong>{formatDateTime(selectedJourney.submittedAt)}</strong>
                </div>
                <div className="visitorJourneyModalStat">
                  <span>Joined</span>
                  <strong>{formatDateTime(selectedJourney.linkedAt)}</strong>
                </div>
              </div>

              <div className="visitorJourneyModalProgressWrap">
                <div className="visitorJourneyModalProgressMeta">
                  <span>Journey mapped</span>
                  <strong>{formatPercent(selectedJourney.journeyProgress)}</strong>
                </div>
                <ProgressBar now={selectedJourney.journeyProgress} className="visitorJourneyModalProgress" />
              </div>

              <div className="visitorJourneyStepper">
                {selectedJourney.stepper.map((step) => (
                  <div key={step.key} className={`visitorJourneyStep ${step.active ? 'is-active' : 'is-inactive'} visitorJourneyStep--${step.tone}`}>
                    <span>{step.index}</span>
                    <strong>{step.label}</strong>
                    <small>{step.timestamp ? formatDateTime(step.timestamp) : 'Pending'}</small>
                  </div>
                ))}
              </div>

              <div className="visitorJourneyModalGrid">
                <section className="visitorJourneyModalPanel">
                  <div className="visitorJourneyModalPanelHead">
                    <div>
                      <p className="visitorJourneyPanelKicker">Timeline</p>
                      <h5>How the journey unfolded</h5>
                    </div>
                    <Badge bg="light" text="dark">{formatDuration(selectedJourney.journeyDuration)}</Badge>
                  </div>
                  <div className="visitorJourneyTimeline">
                    {selectedJourney.timeline.length ? selectedJourney.timeline.map((item, index) => (
                      <div key={`${item.key || item.label}-${index}`} className={`visitorJourneyTimelineItem visitorJourneyTimelineItem--${item.tone || 'neutral'}`}>
                        <div className="visitorJourneyTimelineDot" />
                        <div className="visitorJourneyTimelineContent">
                          <div className="visitorJourneyTimelineTop">
                            <strong>{item.label}</strong>
                            <span>{formatDateTime(item.timestamp)}</span>
                          </div>
                          <p>{item.detail || 'No additional detail recorded.'}</p>
                        </div>
                      </div>
                    )) : (
                      <div className="visitorJourneyEmpty">No journey milestones were captured for this visitor.</div>
                    )}
                  </div>
                </section>

                <section className="visitorJourneyModalPanel">
                  <div className="visitorJourneyModalPanelHead">
                    <div>
                      <p className="visitorJourneyPanelKicker">Session trail</p>
                      <h5>Visits, pages, and event counts</h5>
                    </div>
                  </div>
                  <div className="table-responsive">
                    <Table hover className="visitorJourneyTable visitorJourneyTable--compact">
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Started</th>
                          <th>Duration</th>
                          <th>Pages</th>
                          <th>Events</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedJourney.sessions.length ? selectedJourney.sessions.map((session) => (
                          <tr key={session.sessionId}>
                            <td>
                              <strong>{shortId(session.sessionId)}</strong>
                              <div className="visitorJourneyTableSubtext">
                                {session.landingPage || 'Landing page not recorded'}
                              </div>
                            </td>
                            <td>{formatDateTime(session.startAt)}</td>
                            <td>{formatDuration(session.duration)}</td>
                            <td>{formatCount(session.pagesViewed)}</td>
                            <td>{formatCount(session.eventCount)}</td>
                          </tr>
                        )) : (
                          <tr>
                            <td colSpan="5" className="visitorJourneyEmptyCell">No session trail is available yet.</td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>
                </section>
              </div>

              <section className="visitorJourneyModalPanel visitorJourneyModalPanel--full">
                <div className="visitorJourneyModalPanelHead">
                  <div>
                    <p className="visitorJourneyPanelKicker">Raw signals</p>
                    <h5>Key events captured on this journey</h5>
                  </div>
                </div>
                <div className="visitorJourneySignalChips">
                  {selectedJourney.signals.length ? selectedJourney.signals.map((signal, index) => (
                    <div key={`${signal.label}-${signal.timestamp}-${index}`} className="visitorJourneySignalChip">
                      <Badge bg={
                        signal.label === 'submitted_application' ? 'success'
                          : signal.label === 'application_abandoned' ? 'danger'
                            : signal.label === 'application_submit_review' ? 'warning'
                              : signal.label === 'application_field_progressed' ? 'primary'
                                : 'dark'
                      }>
                        {signal.label}
                      </Badge>
                      <span>{formatDateTime(signal.timestamp)}</span>
                      <small>{signal.detail || 'No page captured'}</small>
                    </div>
                  )) : (
                    <div className="visitorJourneyEmpty">No raw signals were captured for this journey.</div>
                  )}
                </div>
              </section>
            </div>
          ) : (
            <div className="visitorJourneyLoading visitorJourneyLoading--modal">
              <Spinner animation="border" role="status" />
              <p>Loading journey details...</p>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default VisitorJourneyIntelligence;
